// @ts-check
// Texto formatado das notas (doc 10, seção 7; contrato 08, seção 5): o corpo fica em markdown no `bodyV2.markdown`,
// como o Twenty grava ao exportar do BlockNote, e o editor trabalha em HTML. As duas conversões aqui são pequenas e
// fechadas: o HTML gerado sai de markdown com todo texto escapado e só links http, https e mailto.

const SAFE_URL_PATTERN = /^(https?:\/\/|mailto:)/i;
// menções (shared/rich-text/mentions.js): mention:<objeto>/<id>, com id e objeto sem caracteres especiais
const MENTION_URL_PATTERN = /^mention:([A-Za-z]\w*)\/([\w-]+)$/;

/** @param {string} text */
function escapeHtml(text) {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/**
 * Marcas de linha: negrito, itálico, tachado, código e links. Recebe texto já escapado.
 * @param {string} escaped
 */
function renderInline(escaped) {
  const codeSpans = /** @type {string[]} */ ([]);
  let text = escaped.replace(/`([^`]+)`/g, (_match, code) => {
    codeSpans.push(`<code>${code}</code>`);
    return `\u0000${codeSpans.length - 1}\u0000`;
  });
  text = text
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (match, label, url) => {
      const decodedUrl = url.replace(/&amp;/g, '&');
      const mention = MENTION_URL_PATTERN.exec(decodedUrl);
      if (mention) {
        // membro da equipe não tem página; registro mencionado abre como link interno
        const href = mention[1] === 'workspaceMember' ? '' : ` href="/object/${mention[1]}/${mention[2]}"`;
        return `<a class="mention-chip" data-mention="${mention[1]}/${mention[2]}" contenteditable="false"${href}>${label}</a>`;
      }
      return SAFE_URL_PATTERN.test(decodedUrl) ? `<a href="${url}" target="_blank" rel="noopener noreferrer">${label}</a>` : match;
    })
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/~~([^~]+)~~/g, '<s>$1</s>')
    .replace(/(^|[^*\w])\*([^*\s][^*]*)\*/g, '$1<em>$2</em>')
    .replace(/(^|[^_\w])_([^_\s][^_]*)_/g, '$1<em>$2</em>');
  return text.replace(/\u0000(\d+)\u0000/g, (_match, index) => codeSpans[Number(index)]);
}

/**
 * Markdown (subconjunto: títulos, listas, lista de tarefas, citação, parágrafos e marcas de linha) para HTML seguro.
 * @param {string | null | undefined} markdown
 */
export function markdownToHtml(markdown) {
  const lines = String(markdown ?? '').replace(/\r\n?/g, '\n').split('\n');
  /** @type {string[]} */
  const output = [];
  /** @type {{ tag: 'ul' | 'ol', isChecklist: boolean } | null} */
  let openList = null;
  /** @type {string[]} */
  let paragraph = [];
  const flushParagraph = () => {
    if (paragraph.length > 0) output.push(`<p>${paragraph.map((line) => renderInline(escapeHtml(line))).join('<br>')}</p>`);
    paragraph = [];
  };
  const closeList = () => {
    if (openList) output.push(`</${openList.tag}>`);
    openList = null;
  };
  for (const line of lines) {
    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    const checklist = /^\s*[-*]\s+\[( |x|X)\]\s+(.*)$/.exec(line);
    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    const quote = /^>\s?(.*)$/.exec(line);
    if (/^\s*(-{3,}|\*{3,})\s*$/.test(line)) {
      flushParagraph();
      closeList();
      output.push('<hr>');
    } else if (heading) {
      flushParagraph();
      closeList();
      const level = heading[1].length;
      output.push(`<h${level}>${renderInline(escapeHtml(heading[2]))}</h${level}>`);
    } else if (checklist || bullet || numbered) {
      flushParagraph();
      const tag = numbered && !bullet ? 'ol' : 'ul';
      const isChecklist = Boolean(checklist);
      if (!openList || openList.tag !== tag || openList.isChecklist !== isChecklist) {
        closeList();
        openList = { tag, isChecklist };
        output.push(isChecklist ? '<ul data-checklist="true">' : `<${tag}>`);
      }
      if (checklist) {
        const isChecked = checklist[1].toLowerCase() === 'x';
        output.push(`<li data-checked="${isChecked}">${renderInline(escapeHtml(checklist[2]))}</li>`);
      } else {
        output.push(`<li>${renderInline(escapeHtml((bullet ?? numbered ?? [])[1] ?? ''))}</li>`);
      }
    } else if (quote) {
      flushParagraph();
      closeList();
      output.push(`<blockquote>${renderInline(escapeHtml(quote[1]))}</blockquote>`);
    } else if (line.trim() === '') {
      flushParagraph();
      closeList();
    } else {
      closeList();
      paragraph.push(line);
    }
  }
  flushParagraph();
  closeList();
  return output.join('');
}

/**
 * Texto de um nó com as marcas de linha em markdown.
 * @param {Node} node
 * @returns {string}
 */
function inlineMarkdown(node) {
  if (node.nodeType === 3) return (node.textContent ?? '').replace(/ /g, ' ');
  if (node.nodeType !== 1) return '';
  const element = /** @type {HTMLElement} */ (node);
  const content = [...element.childNodes].map(inlineMarkdown).join('');
  switch (element.tagName) {
    case 'BR':
      return '\n';
    case 'STRONG':
    case 'B':
      return content.trim() ? `**${content}**` : content;
    case 'EM':
    case 'I':
      return content.trim() ? `*${content}*` : content;
    case 'S':
    case 'STRIKE':
    case 'DEL':
      return content.trim() ? `~~${content}~~` : content;
    case 'CODE':
      return content ? `\`${content}\`` : '';
    case 'A': {
      const mention = element.dataset.mention;
      if (mention && MENTION_URL_PATTERN.test(`mention:${mention}`)) return `[${content.replace(/[\[\]]/g, '')}](mention:${mention})`;
      const href = element.getAttribute('href') ?? '';
      return SAFE_URL_PATTERN.test(href) ? `[${content}](${href})` : content;
    }
    default:
      return content;
  }
}

/**
 * HTML do editor (contenteditable) para markdown. Blocos desconhecidos viram parágrafos.
 * @param {HTMLElement} root
 */
export function htmlToMarkdown(root) {
  /** @type {string[]} */
  const blocks = [];
  /** @type {string[]} */
  let loose = [];
  const flushLoose = () => {
    const text = loose.join('').trim();
    if (text) blocks.push(text);
    loose = [];
  };
  for (const node of [...root.childNodes]) {
    if (node.nodeType !== 1) {
      loose.push(inlineMarkdown(node));
      continue;
    }
    const element = /** @type {HTMLElement} */ (node);
    const tag = element.tagName;
    if (['B', 'STRONG', 'I', 'EM', 'S', 'STRIKE', 'DEL', 'CODE', 'A', 'SPAN', 'BR'].includes(tag)) {
      loose.push(inlineMarkdown(element));
      continue;
    }
    flushLoose();
    if (tag === 'HR') {
      blocks.push('---');
    } else if (/^H[1-6]$/.test(tag)) {
      const level = Math.min(3, Number(tag.slice(1)));
      blocks.push(`${'#'.repeat(level)} ${inlineMarkdown(element).trim()}`);
    } else if (tag === 'UL' || tag === 'OL') {
      const isChecklist = element.dataset.checklist === 'true';
      const items = [...element.children].filter((child) => child.tagName === 'LI').map((item, index) => {
        const text = inlineMarkdown(item).trim().replace(/\n+/g, ' ');
        if (isChecklist) return `- [${/** @type {HTMLElement} */ (item).dataset.checked === 'true' ? 'x' : ' '}] ${text}`;
        return tag === 'OL' ? `${index + 1}. ${text}` : `- ${text}`;
      });
      if (items.length > 0) blocks.push(items.join('\n'));
    } else if (tag === 'BLOCKQUOTE') {
      const text = inlineMarkdown(element).trim();
      if (text) blocks.push(text.split('\n').map((line) => `> ${line}`).join('\n'));
    } else {
      const text = inlineMarkdown(element).replace(/\n+$/, '').trim();
      if (text) blocks.push(text);
    }
  }
  flushLoose();
  return blocks.join('\n\n');
}

/**
 * Texto corrido para a prévia do cartão da nota, sem marcas de markdown.
 * @param {string | null | undefined} markdown
 */
export function markdownToPlainText(markdown) {
  return String(markdown ?? '')
    .replace(/^#{1,3}\s+/gm, '')
    .replace(/^\s*[-*]\s+\[( |x|X)\]\s+/gm, '')
    .replace(/^\s*([-*]|\d+[.)])\s+/gm, '')
    .replace(/^>\s?/gm, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/(\*\*|~~|`)/g, '')
    .replace(/(^|\W)[*_]([^*_]+)[*_]/g, '$1$2')
    .replace(/\n{2,}/g, '\n')
    .trim();
}
